import React, { useState } from 'react';
import { TrendingUp, DollarSign, Edit2, X, HelpCircle } from 'lucide-react';
import { formatNumber, getTotalPoints, getTotalFees } from '../../utils';

export const ROITab = ({ weeklyData = {}, projectSettings = {}, setProjectSettings, isDark, colors }) => {
  const [editingProject, setEditingProject] = useState(null);
  const [editForm, setEditForm] = useState({ bearish: '', base: '', bullish: '' });
  const [showHelp, setShowHelp] = useState(false);

  const allProjects = Object.keys(projectSettings);

  const getMetricLabelShort = (settings) => {
    const metricType = settings?.metricType || 'points';
    if (metricType === 'custom' && settings?.customMetricName) {
      return settings.customMetricName.toLowerCase();
    }
    switch (metricType) {
      case 'points': return 'point';
      case 'volume': return 'vol';
      case 'fees': return 'fee';
      default: return 'point';
    }
  };

  const getMetricLabelPlural = (settings) => {
    const metricType = settings?.metricType || 'points';
    if (metricType === 'custom' && settings?.customMetricName) {
      return settings.customMetricName;
    }
    switch (metricType) {
      case 'points': return 'points';
      case 'volume': return 'volume';
      case 'fees': return 'fees earned';
      default: return 'points';
    }
  };

  const getMetricValue = (projectName, settings) => {
    const metricType = settings?.metricType || 'points';
    switch (metricType) {
      case 'points': return getTotalPoints(weeklyData, projectName);
      case 'volume': return getTotalVolume(projectName);
      case 'fees': return getTotalFees(weeklyData, projectName);
      case 'custom': return getTotalCustomMetric(projectName);
      default: return getTotalPoints(weeklyData, projectName);
    }
  };

  const getTotalVolume = (projectKey) => {
    let total = 0;
    Object.values(weeklyData?.weeks || {}).forEach(weekEntries => {
      weekEntries.forEach(entry => {
        if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
          total += entry.volume || 0;
        }
      });
    });
    return total;
  };

  const getTotalCustomMetric = (projectKey) => {
    let total = 0;
    Object.values(weeklyData?.weeks || {}).forEach(weekEntries => {
      weekEntries.forEach(entry => {
        if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
          total += entry.customMetric || 0;
        }
      });
    });
    return total;
  };

  const calculatePortfolioROI = () => {
    let bearish = 0;
    let base = 0;
    let bullish = 0;

    allProjects.forEach(projectName => {
      const settings = projectSettings[projectName];
      const metricValue = getMetricValue(projectName, settings);
      
      if (settings?.roiScenarios && metricValue > 0) {
        bearish += metricValue * (settings.roiScenarios.bearish || 0);
        base += metricValue * (settings.roiScenarios.base || 0);
        bullish += metricValue * (settings.roiScenarios.bullish || 0);
      }
    });

    return { bearish, base, bullish };
  };

  const portfolioROI = calculatePortfolioROI();

  const startEditScenarios = (projectName) => {
    const settings = projectSettings[projectName];
    setEditingProject(projectName);
    setEditForm({
      bearish: settings?.roiScenarios?.bearish || '',
      base: settings?.roiScenarios?.base || '',
      bullish: settings?.roiScenarios?.bullish || ''
    });
  };

  const saveScenarios = () => {
    if (!editingProject) return;

    const updatedSettings = {
      ...projectSettings[editingProject],
      roiScenarios: {
        bearish: parseFloat(editForm.bearish) || 0,
        base: parseFloat(editForm.base) || 0,
        bullish: parseFloat(editForm.bullish) || 0
      }
    };

    setProjectSettings({
      ...projectSettings,
      [editingProject]: updatedSettings
    });

    setEditingProject(null);
    setEditForm({ bearish: '', base: '', bullish: '' });
  };

  const cancelEdit = () => {
    setEditingProject(null);
    setEditForm({ bearish: '', base: '', bullish: '' });
  };

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-2xl font-bold flex items-center gap-2 ${colors.textPrimary}`}>
            <TrendingUp size={28} className="text-green-400" />
            ROI Projections
          </h3>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className={`transition-colors ${colors.textSecondary} hover:${colors.textPrimary}`}
          >
            <HelpCircle size={24} />
          </button>
        </div>

        {showHelp && (
          <div className={`rounded-lg p-4 mb-6 text-sm border ${isDark ? 'bg-blue-900/20 border-blue-700/30 text-slate-300' : 'bg-blue-50 border-blue-200 text-gray-700'}`}>
            <h4 className={`font-semibold mb-2 ${colors.textPrimary}`}>💡 How do scenarios work?</h4>
            <ul className="space-y-2">
              <li><span className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>🔴 Bearish:</span> Pessimistic scenario - lowest expected airdrop value</li>
              <li><span className={`font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>🟡 Base:</span> Realistic scenario - most likely airdrop value</li>
              <li><span className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>🟢 Bullish:</span> Optimistic scenario - maximum expected airdrop value</li>
            </ul>
            <p className={`mt-3 ${colors.textSecondary}`}>For each unit of your tracked metric, define how much USD you expect to receive in the airdrop</p>
          </div>
        )}

        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
          <h4 className={`text-lg font-semibold mb-4 ${colors.textSecondary}`}>Total Estimated Portfolio</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`rounded-lg p-4 text-center border ${isDark ? 'bg-red-900/20 border-red-700/30' : 'bg-red-50 border-red-200'}`}>
              <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>🔴 Bearish</div>
              <div className={`text-2xl font-bold ${colors.textPrimary}`}>${formatNumber(portfolioROI.bearish)}</div>
            </div>
            <div className={`rounded-lg p-4 text-center border ${isDark ? 'bg-yellow-900/20 border-yellow-700/30' : 'bg-yellow-50 border-yellow-200'}`}>
              <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>🟡 Base</div>
              <div className={`text-3xl font-bold ${colors.textPrimary}`}>${formatNumber(portfolioROI.base)}</div>
            </div>
            <div className={`rounded-lg p-4 text-center border ${isDark ? 'bg-green-900/20 border-green-700/30' : 'bg-green-50 border-green-200'}`}>
              <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>🟢 Bullish</div>
              <div className={`text-2xl font-bold ${colors.textPrimary}`}>${formatNumber(portfolioROI.bullish)}</div>
            </div>
          </div>
        </div>

        {allProjects.length === 0 ? (
          <div className={`text-center py-12 ${colors.textSecondary}`}>
            <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
            <p>No projects configured yet</p>
            <p className="text-sm mt-2">Add airdrops in the "Airdrops" tab to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {allProjects.map(projectName => {
              const settings = projectSettings[projectName] || {};
              const metricLabel = getMetricLabelShort(settings);
              const metricLabelPlural = getMetricLabelPlural(settings);
              const metricValue = getMetricValue(projectName, settings);
              const isEditing = editingProject === projectName;
              const hasScenarios = settings.roiScenarios && (settings.roiScenarios.bearish || settings.roiScenarios.base || settings.roiScenarios.bullish);

              const projectROI = hasScenarios ? {
                bearish: metricValue * (settings.roiScenarios.bearish || 0),
                base: metricValue * (settings.roiScenarios.base || 0),
                bullish: metricValue * (settings.roiScenarios.bullish || 0)
              } : null;

              return (
                <div key={projectName} className={`rounded-lg p-5 border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className={`text-xl font-bold ${colors.textPrimary}`}>{projectName}</h4>
                      <div className={`text-sm mt-1 ${colors.textSecondary}`}>
                        {formatNumber(metricValue)} {metricLabelPlural} • ${formatNumber(settings.capital || 0)} invested
                      </div>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => startEditScenarios(projectName)}
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-sm transition-colors"
                      >
                        <Edit2 size={16} />
                        {hasScenarios ? 'Edit Scenarios' : 'Configure Scenarios'}
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                      <h5 className={`font-semibold mb-4 ${colors.textPrimary}`}>Configure ROI Scenarios</h5>
                      <p className={`text-sm mb-4 ${colors.textSecondary}`}>
                        For each <strong>{metricLabel}</strong> earned, how much USD do you expect to receive in the airdrop?
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className={`block text-sm mb-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                            🔴 Bearish ($/{metricLabel})
                          </label>
                          <input
                            type="number"
                            value={editForm.bearish}
                            onChange={(e) => setEditForm({ ...editForm, bearish: e.target.value })}
                            placeholder="0.10"
                            step="0.01"
                            className={`w-full border rounded px-3 py-2 focus:border-red-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm mb-2 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                            🟡 Base ($/{metricLabel})
                          </label>
                          <input
                            type="number"
                            value={editForm.base}
                            onChange={(e) => setEditForm({ ...editForm, base: e.target.value })}
                            placeholder="0.50"
                            step="0.01"
                            className={`w-full border rounded px-3 py-2 focus:border-yellow-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                            🟢 Bullish ($/{metricLabel})
                          </label>
                          <input
                            type="number"
                            value={editForm.bullish}
                            onChange={(e) => setEditForm({ ...editForm, bullish: e.target.value })}
                            placeholder="1.00"
                            step="0.01"
                            className={`w-full border rounded px-3 py-2 focus:border-green-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                      </div>

                      <div className={`text-xs mb-4 p-3 rounded ${isDark ? 'bg-slate-700/50 text-slate-400' : 'bg-gray-200 text-gray-600'}`}>
                        💡 Example: If you have {formatNumber(metricValue)} {metricLabelPlural} and set Base to $0.50/{metricLabel}, your estimated airdrop = ${formatNumber(metricValue * 0.5)}
                      </div>

                      <div className="flex gap-2">
                        <button onClick={saveScenarios} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">
                          Save
                        </button>
                        <button onClick={cancelEdit} className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                          <X size={18} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : hasScenarios ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className={`rounded-lg p-3 border ${isDark ? 'bg-red-900/10 border-red-700/30' : 'bg-red-50 border-red-200'}`}>
                        <div className={`text-xs mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                          🔴 Bearish (${settings.roiScenarios.bearish}/{metricLabel})
                        </div>
                        <div className={`text-lg font-bold ${colors.textPrimary}`}>${formatNumber(projectROI.bearish)}</div>
                      </div>
                      <div className={`rounded-lg p-3 border ${isDark ? 'bg-yellow-900/10 border-yellow-700/30' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div className={`text-xs mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                          🟡 Base (${settings.roiScenarios.base}/{metricLabel})
                        </div>
                        <div className={`text-lg font-bold ${colors.textPrimary}`}>${formatNumber(projectROI.base)}</div>
                      </div>
                      <div className={`rounded-lg p-3 border ${isDark ? 'bg-green-900/10 border-green-700/30' : 'bg-green-50 border-green-200'}`}>
                        <div className={`text-xs mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          🟢 Bullish (${settings.roiScenarios.bullish}/{metricLabel})
                        </div>
                        <div className={`text-lg font-bold ${colors.textPrimary}`}>${formatNumber(projectROI.bullish)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className={`rounded-lg p-4 text-center ${isDark ? 'bg-slate-800/30' : 'bg-gray-100'} ${colors.textSecondary}`}>
                      <p>You haven't configured ROI scenarios for this project</p>
                      <p className="text-sm mt-1">Click "Configure Scenarios" to estimate your airdrop value</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};